// src/components/formio/GoogleCaptcha.js
import { Components } from "formiojs";

export default class GoogleCaptcha extends Components.components.field {
    constructor(component, options, data) {
        super(component, options, data);
        this.widgetId = null;
        this.attachedOnce = false;
    }

    static schema(...extend) {
        return super.schema({
                type: "googlecaptcha",
                label: "Google reCAPTCHA",
                key: "googlecaptcha",
                customClass: "google-recaptcha",
                input: true,
                tableView: false,
                siteKey: "6Ld5GY8rAAAAANAK4C6KWaK3Ds3kRBMJUE6vIqDn",
                theme: "light",
                size: "normal",
            },
            ...extend
        );
    }

    static get builderInfo() {
        return {
            title: "Google reCAPTCHA",
            group: "advanced",
            icon: "shield-alt",
            weight: 100,
            schema: GoogleCaptcha.schema(),
        };
    }

    static editForm(...extend) {
        // gleiche Technik wie ConnectToIconManager: Basis-Edit-Form holen und Felder in Display-Panel injizieren
        const base = Components.components.button.editForm(...extend);

        if (base && base.components && base.components.length > 0) {
            const firstTab = base.components[0];
            const tabItems = firstTab.components || [];
            let displayPanel = null;

            for (let i = 0; i < tabItems.length; i++) {
                if (tabItems[i].key === "display") {
                    displayPanel = tabItems[i];
                    break;
                }
            }

            if (displayPanel && Array.isArray(displayPanel.components)) {
                const comps = displayPanel.components;
                let labelIndex = -1;
                for (let i = 0; i < comps.length; i++) {
                    if (comps[i].key === "label") {
                        labelIndex = i;
                        break;
                    }
                }
                const insertAt = labelIndex >= 0 ? labelIndex + 1 : comps.length;

                // nur GoogleCaptcha-spezifische Felder einfügen
                comps.splice(
                    insertAt,
                    0, {
                        type: "textfield",
                        input: true,
                        key: "siteKey",
                        label: "Site Key",
                        placeholder: "Google reCAPTCHA site key",
                        validate: { required: true },
                    }, {
                        type: "select",
                        input: true,
                        key: "theme",
                        label: "Theme",
                        data: {
                            values: [
                                { label: "Light", value: "light" },
                                { label: "Dark", value: "dark" },
                            ],
                        },
                        defaultValue: "light",
                    }, {
                        type: "select",
                        input: true,
                        key: "size",
                        label: "Size",
                        data: {
                            values: [
                                { label: "Normal", value: "normal" },
                                { label: "Compact", value: "compact" },
                            ],
                        },
                        defaultValue: "normal",
                    }
                );
            }
        }
        return base;
    }

    render(children) {
        // Script lazy laden, einmalig
        if (!document.getElementById("recaptcha-script")) {
            const s = document.createElement("script");
            s.src =
                "https://www.google.com/recaptcha/api.js?onload=onRecaptchaLoadCallback&render=explicit";
            s.async = true;
            s.defer = true;
            s.id = "recaptcha-script";
            document.head.appendChild(s);
        }

        // identische Render-Signatur wie Button-Komponente: super.render(children)
        return super.render(
            `
      <div class="google-recaptcha-wrapper">
        <div class="google-recaptcha-container" id="recaptcha-${this.id}"></div>
        <div class="text-danger mt-2" id="recaptcha-error-${this.id}"></div>
      </div>
    `,
            children
        );
    }

    attach(element) {
        const result = super.attach(element);
        if (this.attachedOnce) return result;
        this.attachedOnce = true;

        const elId = `recaptcha-${this.id}`;
        const errorId = `recaptcha-error-${this.id}`;
        const siteKey = this.component.siteKey || "";

        const showError = (msg) => {
            const e = element.querySelector(`#${errorId}`);
            if (e) e.innerText = msg || "";
        };

        const renderCaptcha = () => {
            const container = document.getElementById(elId);
            if (!siteKey) {
                showError("Site Key fehlt.");
                return;
            }
            if (!container || container.childElementCount > 0 || this.component.disabled) return;

            try {
                this.widgetId = window.grecaptcha.render(elId, {
                    sitekey: siteKey,
                    theme: this.component.theme || "light",
                    size: this.component.size || "normal",
                    callback: (response) => {
                        showError("");
                        this.setValue(response);
                        this.triggerChange();
                    },
                    "expired-callback": () => {
                        this.setValue("");
                        this.triggerChange();
                    },
                    "error-callback": () => {
                        showError("reCAPTCHA konnte nicht geladen werden.");
                    },
                });
            } catch (err) {
                showError("Fehler beim Initialisieren des reCAPTCHA.");
                // eslint-disable-next-line no-console
                console.error(err);
            }
        };

        if (!window.onRecaptchaLoadCallback) {
            window.onRecaptchaLoadCallback = () => {
                if (window.grecaptcha.render) renderCaptcha();
            };
        }
        if (window.grecaptcha.render) setTimeout(renderCaptcha, 0);

        return result;
    }

    destroy() {
        if (this.widgetId != null && window.grecaptcha.reset) {
            try {
                window.grecaptcha.reset(this.widgetId);
            } catch (_) {}
            this.widgetId = null;
        }
        return super.destroy();
    }

    isEmpty(v) {
        return !v;
    }
    getValue() {
        return this.dataValue || "";
    }
    setValue(value, flags = {}) {
        const changed = this.dataValue !== value;
        this.dataValue = value;
        if (changed && !flags.noUpdate) this.updateValue(value, flags);
        return changed;
    }

    validate(required, value) {
        const v = this.component.validate || {};
        if (v.required && !this.getValue()) {
            const msg =
                this.component.errorLabel ||
                "Bitte reCAPTCHA abschließen, bevor Sie senden.";
            return [{ level: "error", message: msg }];
        }
        return [];
    }
}